import { stores } from '@kurtosys/app-start';
import { components } from '@kurtosys/ksys-app-components';
import { common } from '@kurtosys/ksys-app-template';
import { IInputs } from '@kurtosys/types/app/IInputs.js';
import { IManifest } from '@kurtosys/types/appsManager/index.js';
import { action, computed, makeObservable } from 'mobx';

import { IComponentStyles } from '../../../models/app/IComponentStyles.js';
import { IConfiguration } from '../../../models/app/IConfiguration.js';
import { TStoreContext } from '../../../models/app/TStoreContext.js';
import { AppStore } from '../../App/stores/AppStore.js';
import { QuizStore } from '../../App/stores/QuizStore.js';
import { IHeaderConfiguration } from '../models';
type IApplicationEmbedInput = components.base.ApplicationEmbed.models.IApplicationEmbedInput;
type IGridProps = components.base.Grid.models.IGridProps;

const StoreBase = stores.base.StoreBase<IConfiguration, IComponentStyles>;

type QueryStore = stores.QueryStore<IConfiguration, IComponentStyles>;

export class HeaderStore extends StoreBase {
	static componentKey = 'header' as const;

	constructor(storeContext: TStoreContext, manifest: IManifest) {
		super(storeContext, manifest);
		makeObservable(this);
	}

	@computed
	get appStore() {
		return this.storeContext.get<AppStore>('appStore');
	}

	@computed
	get quizStore() {
		return this.storeContext.get<QuizStore>('quizStore');
	}

	@computed
	get queryStore() {
		return this.storeContext.get<QueryStore>('queryStore');
	}

	@computed
	get configuration(): IHeaderConfiguration | undefined {
		if (this.storeContext && this.appStore) {
			return this.appStore.getComponentConfiguration(HeaderStore.componentKey);
		}
	}

	@action
	async initialize(): Promise<void> {
		return;
	}

	@computed
	get rawGridProps(): IGridProps | undefined {
		if (this.quizStore.isComplete) {
			return this.configuration && this.configuration.resultsGridProps;
		}
		return this.configuration && this.configuration.gridProps;
	}

	@computed
	get gridProps(): IGridProps | undefined {
		const gridProps: IGridProps = {
			...common.commonUtils.deepMergeObjects({}, this.rawGridProps),
		};
		if (!common.commonUtils.isNullOrEmpty(gridProps.breakpoints)) {
			gridProps.breakpoints.forEach((breakpoint) => {
				if (breakpoint.props) {
					this.orchestrateGridProps(breakpoint.props);
					breakpoint.props.queryStore = this.queryStore;
					breakpoint.props.loadUiIds = true;
				}
			});
		}
		this.orchestrateGridProps(gridProps);
		gridProps.queryStore = this.queryStore;
		(gridProps.queryStore as QueryStore).responseContext = this.responseContext;
		gridProps.loadUiIds = true;

		return gridProps;
	}

	@computed
	get responseContext(): any {
		return {
			total: this.quizStore.totalQuestions,
			current: this.quizStore.currentQuestion + 1,
			score: this.quizStore.score,
			percentage: this.quizStore.percentage,
		};
	}

	orchestrateGridProps = (gridProps: IGridProps) => {
		const { grid } = gridProps;

		if (grid) {
			const inputs: Partial<IInputs> = this.appStore.appParamsHelper.inputs || {};
			const embedInputs: IApplicationEmbedInput[] = (Object.keys(inputs) as (keyof IInputs)[]).map((key) => {
				const value = inputs[key] as any;
				return {
					key,
					value,
				};
			});
			const { rows = [] } = grid;
			rows.forEach((row) => {
				if (row) {
					const { columns = [] } = row;
					columns.forEach((column) => {
						if (column) {
							const { components = [] } = column;
							components.forEach((component) => {
								if (component) {
									switch (component.key) {
										case 'base.applicationEmbed':
											component.props = this.appStore.orchestrateEmbed(
												component.props,
												'ksys-app-other',
												embedInputs,
											);
											break;
									}
								}
							});
						}
					});
				}
			});
		}
		return gridProps;
	};
}
